---
title: "Which PDF converter to use for Excel, text and images"
description: "PDF converters do very different jobs: Excel to PDF, PDF to JPG, PDF to text, compression and rotation. Here is how to pick the right one."
locale: "en"
date: "2026-06-16"
tags: ["pdf", "converter", "tools"]
relatedTools: ["excelVPdf", "vJpg", "szhat", "povernut"]
translations:
  ru: "kakoj-pdf-konverter-vybrat"
faq:
  - q: "Is there one universal PDF converter?"
    a: "There are catalog pages, but the operations are different. Excel to PDF, PDF to JPG, PDF to text and PDF compression give different results and have different limits."
  - q: "Which operations can run locally?"
    a: "On pdfy, all of them: rotation, compression, PDF to JPG, PDF to text, Excel to PDF and page editing run in the browser. The file is not uploaded."
  - q: "What about Word and PowerPoint?"
    a: "They need a full office engine, so pdfy does not convert them. Save the file as PDF in Word or PowerPoint, then compress or edit it here."
  - q: "Where should I start if I am not sure?"
    a: "Open the PDF converter page and choose by input file: PDF, Excel, HTML or image. For a simple job, a specific tool is faster than a universal form."
---

`document_final.pdf` has to be "converted". Converted into what is the
missing part. Accounting wants the text, marketing wants images for a
product card, and the upload form wants the file under 5 MB. People call
all of that a PDF converter. In practice, those are different operations
with different ways to fail.

The catalog entry is here: [PDF converter](/pdf-konverter). After that,
pick by input and output, not by the biggest button on the page.

## PDF to text

[PDF to text](/pdf-v-text) is for copying content and editing it somewhere
else. It works with PDFs that already contain a text layer: contracts,
instructions, proposals, reports exported from Word. Formatting is lost,
but the text is ready to paste into an editor.

A quick check: can you select a line of text in the PDF viewer? If the
whole page behaves like one image, it is a scan with no text layer, and
the tool will tell you so.

## Excel and HTML to PDF

[Excel to PDF](/excel-v-pdf) and [HTML to PDF](/html-v-pdf) solve the
opposite problem: freeze data so the recipient sees the same thing. The
spreadsheet is built right in the browser: cell values are carried over,
colors and merged cells are not. If the exact look matters, save the PDF
from Excel itself.

Word and PowerPoint work the same way: `File → Save As → PDF` in the
editor. Browsers are bad at recalculating fonts, line breaks, headers and
slide sizes, so pdfy does not convert those files.

## PDF to JPG

[PDF to JPG](/v-jpg) is a different animal. The PDF does not need to be
understood as an office document. It needs to be drawn as pages and saved
as images. That fits the browser well: pdf.js reads pages, canvas draws,
JPG downloads locally.

Use it for previews, chat messages, product cards, one-page instructions.
Do not use it as an archive format for contracts or documents with links.
After conversion, text is no longer selectable. It is just pixels.

## Compression and rotation

[Rotate PDF](/povernut) is the lightest operation. Often it only changes
the `/Rotate` field, so the file does not need a full rebuild. That is
why rotation is fast and local.

[Compress PDF](/szhat) is messier. The browser recompresses images inside
the file, and that is enough for many everyday scans. Text and vector
graphics barely shrink: if the file is made of those, the tool says so.

## My default choice

Need the text? PDF to text. Need to send a table with no surprises? Excel
to PDF. Need an image? PDF to JPG. Upload form rejects the size? Compress.
Scan is sideways? Rotate.

A universal PDF converter is useful as a map. The actual job should go
through the specific tool that says what the operation cannot promise.
